import { Controller } from "@hotwired/stimulus"
import { KlavyAgentCore } from "../lib/klavy_agent_core"

export default class extends Controller {
  static values = {
    agentSlug:        String,
    agentId:          Number,
    resourceType:     String,
    resourceId:       { type: Number, default: 0 },
    contextType:      { type: String, default: "page" },
    sectionId:        { type: Number, default: 0 },
    sectionType:      { type: String, default: "" },
    sectionTitle:     { type: String, default: "" },
    systemPrompt:     String,
    formSelector:     String,
    fieldPrefix:      String,
    welcomeMessage:   String,
    quickStarts:      { type: String, default: "[]" },
    existingSections: { type: String, default: "[]" },
    siteStructure:    { type: String, default: "{}" },
    pageTitle:        { type: String, default: "" },
    pageSlug:         { type: String, default: "" },
    metaDescription:  { type: String, default: "" },
    parentSiteId:     { type: Number, default: 0 },
    parentSiteTitle:  { type: String, default: "" },
    designerBridge:   { type: Boolean, default: false },
    imageModelPicker: { type: Boolean, default: false },
    fromPlanning:     { type: Boolean, default: false },
    chatApiPath:      { type: String, default: "" },
    rulesApiPath:     { type: String, default: "" },
    aiBasePath:       { type: String, default: "" },
    draftApiPath:     { type: String, default: "" },
    imageApiPath:     { type: String, default: "" },
    draftWriterKind:  { type: String, default: "" },
    threadsDraftApiPath: { type: String, default: "" },
    threadsStrategyApiPath: { type: String, default: "" },
    threadsProfileApiPath: { type: String, default: "" },
    kakaoTalkDraftApiPath: { type: String, default: "" },
    kakaoTalkStrategyApiPath: { type: String, default: "" },
    kakaoTalkProfileApiPath: { type: String, default: "" },
    kmongSellerProfileApiPath: { type: String, default: "" },
    kmongWorkspaceTab: { type: String, default: "service" },
    comparisonReportApiPath: { type: String, default: "" },
    comparisonPatchApiPath: { type: String, default: "" },
    comparisonGeneratedImagesApiPath: { type: String, default: "" },
    comparisonDetailImagesApiPath: { type: String, default: "" },
    promptToolsEnabled: { type: Boolean, default: false },
    promptFetchUrlPath: { type: String, default: "" },
    promptExtractDocumentPath: { type: String, default: "" },
    iaGenerateFromUrlPath: { type: String, default: "" },
    planningGenerateFromUrlPath: { type: String, default: "" },
    planningGenerateFromUrlTab: { type: String, default: "" },
    planningInterviewFromUrlPath: { type: String, default: "" },
    promptUrlAddLabel: { type: String, default: "" },
    promptUrlBusyLabel: { type: String, default: "" },
    voiceModelPicker: { type: Boolean, default: false },
    voiceModelMode: { type: String, default: "realtime_voice" },
    voiceModelRuntime: { type: String, default: "" },
  }

  connect() {
    const chatApiPath  = this.chatApiPathValue || this.defaultChatApiPath()
    const rulesApiPath = this.rulesApiPathValue || this.defaultRulesApiPath()

    let quickStarts = []
    let existingSections = []
    let siteStructure = {}
    try { quickStarts      = JSON.parse(this.quickStartsValue || "[]") } catch(e) {}
    try { existingSections = JSON.parse(this.existingSectionsValue || "[]") } catch(e) {}
    try { siteStructure    = JSON.parse(this.siteStructureValue || "{}") } catch(e) {}

    const aiBasePath = this.aiBasePathValue || this.defaultAiBasePath()

    this._core = new KlavyAgentCore({
      container:        this.element,
      agentSlug:        this.agentSlugValue,
      aiBasePath,
      resourceType:     this.resourceTypeValue,
      resourceId:       this.resourceIdValue,
      contextType:      this.contextTypeValue,
      sectionId:        this.sectionIdValue,
      sectionType:      this.sectionTypeValue,
      sectionTitle:     this.sectionTitleValue,
      systemPrompt:     this.systemPromptValue,
      formSelector:     this.formSelectorValue,
      fieldPrefix:      this.fieldPrefixValue,
      welcomeMessage:   this.welcomeMessageValue,
      quickStarts,
      existingSections,
      siteStructure,
      pageTitle:        this.pageTitleValue,
      pageSlug:         this.pageSlugValue,
      metaDescription:  this.metaDescriptionValue,
      parentSiteId:     this.parentSiteIdValue,
      parentSiteTitle:  this.parentSiteTitleValue,
      designerBridge:   this.designerBridgeValue,
      imageModelPicker: this.imageModelPickerValue,
      fromPlanning:     this.fromPlanningValue,
      chatApiPath,
      rulesApiPath,
      draftApiPath: this.draftApiPathValue,
      imageApiPath: this.imageApiPathValue,
      draftWriterKind: this.draftWriterKindValue,
      threadsDraftApiPath: this.threadsDraftApiPathValue,
      threadsStrategyApiPath: this.threadsStrategyApiPathValue,
      threadsProfileApiPath: this.threadsProfileApiPathValue,
      kakaoTalkDraftApiPath: this.kakaoTalkDraftApiPathValue,
      kakaoTalkStrategyApiPath: this.kakaoTalkStrategyApiPathValue,
      kakaoTalkProfileApiPath: this.kakaoTalkProfileApiPathValue,
      kmongSellerProfileApiPath: this.kmongSellerProfileApiPathValue,
      kmongWorkspaceTab: this.kmongWorkspaceTabValue,
      comparisonReportApiPath: this.comparisonReportApiPathValue,
      comparisonPatchApiPath: this.comparisonPatchApiPathValue,
      comparisonGeneratedImagesApiPath: this.comparisonGeneratedImagesApiPathValue,
      comparisonDetailImagesApiPath: this.comparisonDetailImagesApiPathValue,
      promptToolsEnabled: this.promptToolsEnabledValue,
      promptFetchUrlPath: this.promptFetchUrlPathValue,
      promptExtractDocumentPath: this.promptExtractDocumentPathValue,
      iaGenerateFromUrlPath: this.iaGenerateFromUrlPathValue || this.defaultIaGenerateFromUrlPath(),
      planningGenerateFromUrlPath: this.planningGenerateFromUrlPathValue,
      planningGenerateFromUrlTab: this.planningGenerateFromUrlTabValue,
      planningInterviewFromUrlPath: this.planningInterviewFromUrlPathValue,
      promptUrlAddLabel: this.promptUrlAddLabelValue,
      promptUrlBusyLabel: this.promptUrlBusyLabelValue,
      voiceModelPicker: this.voiceModelPickerValue,
      voiceModelMode: this.voiceModelModeValue,
      voiceModelRuntime: this.voiceModelRuntimeValue,
      storagePrefix:    `baAgent_${this.agentSlugValue}`,
    })

    // KlavyAgentCore의 모든 public 메서드를 Stimulus 액션으로 위임
    const proto = Object.getPrototypeOf(this._core)
    Object.getOwnPropertyNames(proto).forEach(method => {
      if (method === "constructor" || method.startsWith("_")) return
      if (typeof this._core[method] !== "function") return
      this[method] = (event) => this._core[method](event)
    })

    // 새 섹션 생성 페이지에서 타입 변경 시 프롬프트 동적 전환
    if (this.contextTypeValue === "section" && !this.sectionIdValue) {
      this._originalSystemPrompt = this.systemPromptValue
      this._sectionTypeListener = (e) => this.handleSectionTypeChange(e)
      window.addEventListener("profile-section-type:changed", this._sectionTypeListener)
    }
  }

  disconnect() {
    this._core?.destroy?.()
    if (this._sectionTypeListener) {
      window.removeEventListener("profile-section-type:changed", this._sectionTypeListener)
    }
  }

  handleSectionTypeChange(event) {
    const type = event.detail?.sectionType
    if (!type || !this._core) return
    const label = event.detail?.sectionLabel || this._profileSectionLabel(type)
    const prompt = type === "ai_block" ? this._aiBlockPrompt() : this._profileSectionPrompt(type, label)
    this._core.updateSectionContext(type, prompt, { sectionTitle: label, quickStarts: [] })
  }

  _profileSectionLabel(type) {
    const labels = {
      hero: "히어로 (프로필 상단)", about: "소개 / About", experience: "경력 / Experience", projects: "프로젝트 / Projects", skills: "스킬 / Skills", contact: "연락처 / Contact", education: "학력 / Education", services: "서비스 / Services", testimonials: "추천글 / Testimonials", stats: "성과 수치 / Stats", gallery: "갤러리 / Gallery", clients: "클라이언트 / Clients", awards: "수상 / Awards", faq: "자주 묻는 질문 / FAQ", cta: "CTA (전환 유도)",
      block_text: "텍스트 블록", block_image: "이미지 블록", block_image_text: "이미지 + 텍스트", block_image_top: "이미지 상단 + 텍스트", block_columns: "다단 컬럼", block_cards: "카드 그리드", block_banner: "배너", block_spacer: "여백", block_divider: "구분선"
    }
    return labels[type] || type || "섹션"
  }

  _profileSectionPrompt(type, label) {
    const fieldsByType = {
      hero: "name, title, tagline, bio_short, photo_url, badge_text, cta_text, cta_url, secondary_cta_text, secondary_cta_url, social_links[{platform, url, icon}]",
      about: "headline, subheadline, bio, photo_url",
      experience: "items[{company, role, period, description}]",
      projects: "items[{title, description, image_url, link_url}]",
      skills: "items[{name, level, category}] 또는 items[{title, description}]",
      contact: "headline, subheadline, email, phone",
      education: "items[{school, degree, period, description}]",
      services: "items[{title, description, icon}]",
      testimonials: "testimonials[{name, role, company, quote, rating}]",
      stats: "items[{number, label}]",
      gallery: "items[{title, image_url, description}]",
      clients: "logos[{name, image_url}]",
      awards: "items[{title, issuer, year, description}]",
      faq: "items[{question, answer}]",
      cta: "headline, subheadline, cta_text, cta_url",
      block_text: "heading, subheading, body_text, alignment, cta_text, cta_url",
      block_image: "image_url, caption, max_width, alignment",
      block_image_text: "image_url, heading, body_text, cta_text, cta_url, layout, image_ratio",
      block_image_top: "image_url, heading, body_text, cta_text, cta_url, image_height",
      block_columns: "column_count, items[{icon, heading, body_text}]",
      block_cards: "column_count, items[{title, description, image_url, link_url}]",
      block_banner: "heading, subheading, cta_text, cta_url, overlay_opacity",
      block_spacer: "height",
      block_divider: "style, width"
    }
    const fields = fieldsByType[type] || "content_data"
    return `## 응답 규칙
- 한국어로 응답하세요.
- 마크다운으로 가독성 있게 설명하세요.
- 이모지를 사용하지 마세요.
- 이것은 프로필 페이지의 섹션 1개를 만드는 화면입니다. 전체 페이지 구성 제안이 아니라 현재 선택된 섹션 1개의 내용만 제안하세요.
- 현재 선택 섹션: ${label} (${type})
- 사용자가 콘텐츠 생성/수정/입력/반영을 요청하면 반드시 마지막에 JSON 블록을 포함하세요.
- JSON은 현재 섹션 타입에 맞는 필드만 사용하세요. 다른 섹션 내용(예: 히어로, 스킬, 프로젝트 전체 묶음)을 섞지 마세요.
- 사용자가 "페이지를 만들고 싶어요"라고 말해도 현재 선택 섹션에 들어갈 내용으로 축소해서 작성하세요.
- 섹션 이름(title)은 관리자가 알아보기 쉬운 한국어 이름으로 함께 제안하세요.
- 사용자가 입력/반영을 요청하면 현재 섹션 폼에 보이는 필드를 최대한 모두 채우세요. 비어 있는 선택 필드도 합리적인 값이 있으면 생략하지 마세요.
- 히어로 이미지/프로필 사진 URL이 필요하면 외부 placeholder URL 대신 `/images/portfolio/profile.png`처럼 앱 내부에서 사용할 수 있는 경로를 사용하세요.

## 현재 섹션 content_data 필드
${fields}

## JSON 형식
\`\`\`json
{"_action":"fill_section","data":{"section_type":"${type}","title":"관리용 섹션 이름","content_data":{}}}
\`\`\`
- content_data에는 위 필드명을 그대로 사용하세요.
- items/testimonials/logos 같은 반복 목록은 배열로 작성하세요.
- 질문이나 방향 제안만 요청한 경우에는 JSON 없이 답변하세요.`
  }

  defaultChatApiPath() {
    if (this.resourceTypeValue === "IaProject" && this.resourceIdValue) {
      return `/ia/projects/${this.resourceIdValue}/chats`
    }

    if (this.resourceTypeValue === "UsProject" && this.resourceIdValue) {
      return `/main-target/projects/${this.resourceIdValue}/chats`
    }

    if (this.resourceTypeValue === "PlanningWorkspaceProject" && this.resourceIdValue) {
      return `/planning-workspace/projects/${this.resourceIdValue}/chats`
    }

    if (this.resourceTypeValue === "ColdmailCampaign" && this.resourceIdValue) {
      return `/coldmail/campaigns/${this.resourceIdValue}/chats`
    }

    if (this.resourceTypeValue === "FacebookAdsCampaign" && this.resourceIdValue) {
      return `/facebook-ads/campaigns/${this.resourceIdValue}/chats`
    }

    if (this.resourceTypeValue === "KakaoTalkChatRoom" && this.resourceIdValue) {
      return `/kakao-talk/chat_rooms/${this.resourceIdValue}/chats`
    }

    if ((this.resourceTypeValue === "KmongServiceRegistrationDraft" || this.resourceTypeValue === "KmongServiceRegistration" || this.resourceTypeValue === "Kmong::ServiceRegistration") && this.resourceIdValue) {
      return `/kmong/service-registrations/${this.resourceIdValue}/chats`
    }

    if (this.resourceTypeValue === "Prompt" && this.resourceIdValue) {
      return `/prompts/${this.resourceIdValue}/chats`
    }

    if (this.resourceTypeValue === "ProfilePage" && this.resourceIdValue) {
      return `/admin/profile_pages/${this.resourceIdValue}/chats`
    }

    if (this.resourceTypeValue === "LandingPage" && this.resourceIdValue) {
      return `/admin/landing_pages/${this.resourceIdValue}/chats`
    }

    if (this.resourceTypeValue === "IaProject" && this.resourceIdValue) {
      return `/admin/klavy_agents/${this.agentIdValue}/chats`
    }

    if (this.agentIdValue) {
      return `/admin/klavy_agents/${this.agentIdValue}/chats`
    }

    return null
  }

  defaultAiBasePath() {
    return this.resourceTypeValue === "IaProject" ? "/ia/ai"
      : this.resourceTypeValue === "UsProject" ? "/main-target/ai"
      : this.resourceTypeValue === "PlanningWorkspaceProject" ? "/planning-workspace/ai"
      : this.resourceTypeValue === "QuotationWorkspaceProject" ? "/quotation-workspace/ai"
      : this.resourceTypeValue === "KakaoTalkChatRoom" ? "/kakao-talk/ai"
      : this.resourceTypeValue === "ColdmailCampaign" ? "/coldmail/ai"
      : this.resourceTypeValue === "FacebookAdsCampaign" ? "/facebook-ads/ai"
      : (this.resourceTypeValue === "KmongServiceRegistrationDraft" || this.resourceTypeValue === "KmongServiceRegistration" || this.resourceTypeValue === "Kmong::ServiceRegistration") ? "/kmong/ai"
      : this.resourceTypeValue === "ImageStudioProject" ? "/image-studio/ai"
      : this.resourceTypeValue === "UiPlanningProject" ? "/ui-planning/ai"
      : this.resourceTypeValue === "UiUxDesignProject" ? "/ui-ux-design/ai"
      : this.resourceTypeValue === "ThreadsCampaign" ? "/threads/ai"
      : this.resourceTypeValue === "Prompt" ? "/prompts/ai"
      : "/admin/ai"
  }

  defaultIaGenerateFromUrlPath() {
    if (this.resourceTypeValue === "IaProject" && this.resourceIdValue) {
      return `/ia/projects/${this.resourceIdValue}/generate_from_url`
    }

    return ""
  }

  _aiBlockPrompt() {
    return `## 응답 규칙
- 한국어로 응답하세요.
- 마크다운으로 가독성 있게 설명하세요.
- 이모지를 사용하지 마세요.
- 사용자가 콘텐츠 생성/수정을 요청하면 반드시 JSON 블록을 포함하세요.
- 질문이나 방향 제안만 요청한 경우에는 JSON 없이 답변하세요.

## 중요: 섹션 단위 생성
- 이것은 프로필 페이지의 **섹션 1개**를 만드는 도구입니다.
- 반드시 하나의 섹션(하나의 주제/목적)만 생성하세요.
- 여러 섹션이나 전체 페이지를 한번에 만들지 마세요.
- 예: "서비스 소개" 요청 → 서비스 소개 섹션 1개만 생성
- 사용자가 "페이지 만들어줘"라고 해도 해당 주제의 섹션 1개만 만드세요.

## AI 블록 HTML 생성 규칙
- 완전한 자체 포함(self-contained) HTML을 생성하세요.
- Tailwind CSS 유틸리티 클래스를 사용하세요.
- border-radius 절대 금지: rounded-* 클래스 사용하지 마세요.
- 외부 이미지 URL 사용 금지: https://images.unsplash.com 등 외부 이미지를 넣지 마세요.
- 이미지가 필요하면 src='__generate__'와 data-image-prompt 속성을 사용하세요.
- 이미지 예시: <img src='__generate__' data-image-prompt='Professional hair salon interior, warm lighting, modern design' class='w-full h-64 object-cover' />
- data-image-prompt는 영어로 구체적으로 작성하세요 (스타일, 분위기, 구도 포함).
- 이미지가 꼭 필요하지 않은 곳은 배경색/그라데이션/인라인 SVG로 대체하세요.
- 폰트: Pretendard Variable (시스템 기본 적용됨)
- 기본 색상: Primary #6366F1, Primary Dark #4F46E5, Accent #F59E0B
- 전체 폭 배경이 필요하면 바깥 div를 w-full로, 콘텐츠만 max-w-[1160px] mx-auto로 감싸세요.
- 예: <div class='w-full bg-[#1a1a1a]'><div class='max-w-[1160px] mx-auto px-6'>콘텐츠</div></div>
- 콘텐츠 전용 섹션은 max-w-[1160px] mx-auto px-4만 사용하세요.
- 반응형 디자인: 모바일/태블릿/데스크톱 대응

## JSON 형식
\`\`\`json
{"_action": "fill_section", "data": {"content_data": {"html_content": "<div class='py-16 bg-white'>...</div>"}}}
\`\`\`
- html_content에 완전한 HTML을 넣으세요.

## 활용 예시 (각각 섹션 1개)
- 서비스 소개 섹션
- 이용방법/프로세스 안내 섹션
- 회사 연혁/소개 섹션
- 쇼핑몰 상품 상세 섹션
- CTA(행동 유도) 섹션
- 프로모션/이벤트 배너 섹션

## 디자인 가이드
- 세련되고 현대적인 웹 디자인을 만드세요.
- 충분한 여백(padding/margin)을 사용하세요.
- 타이포그래피 위계를 명확히 하세요 (text-4xl > text-2xl > text-lg > text-base).
- 그리드 레이아웃을 활용하세요 (grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3).`
  }

  defaultRulesApiPath() {
    if (this.resourceTypeValue === "IaProject" && this.resourceIdValue) {
      return `/ia/projects/${this.resourceIdValue}/klavy_agent_rules`
    }

    if (this.resourceTypeValue === "UsProject") {
      return null
    }

    if (this.resourceTypeValue === "PlanningWorkspaceProject" && this.resourceIdValue) {
      return null
    }

    if (this.resourceTypeValue === "KakaoTalkChatRoom") {
      return null
    }

    if (this.resourceTypeValue === "ColdmailCampaign") {
      return null
    }

    if (this.resourceTypeValue === "Prompt") {
      return null
    }

    if (this.resourceTypeValue === "ProfilePage" && this.resourceIdValue) {
      return `/admin/profile_pages/${this.resourceIdValue}/project_rules`
    }

    if (this.resourceTypeValue === "LandingPage" && this.resourceIdValue) {
      return `/admin/landing_pages/${this.resourceIdValue}/project_rules`
    }

    if (this.resourceTypeValue === "IaProject" && this.resourceIdValue) {
      return `/admin/klavy_agents/${this.agentIdValue}/rules`
    }

    if (this.agentIdValue) {
      return `/admin/klavy_agents/${this.agentIdValue}/rules`
    }

    return null
  }
}
